import React, { useCallback, useEffect, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { CircularProgress, Collapse } from '@material-ui/core';
import { confirm, showAlert } from 'actions/app';
import { setBulkEmailStatus } from 'actions/bulkEmail';
import { StatusManager } from 'common/components/StatusManager';
import { addSequenceSteps, editSequenceStep } from 'services/bulkEmail';
import strings from 'strings';
import { FPIconButton, When } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import { DiscardBulkDialog } from 'UI/components/organisms/BulkEmail/DiscardBulkDialog';
import { useBulkDrawerCommonStyles } from 'UI/components/organisms/BulkEmailDrawer/styles';
import { BulkDrawerFormFieldsMap } from 'UI/components/organisms/BulkEmailForm/fields';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { UIStatus } from 'UI/constants/status';
import { SvgArrowLeft } from 'UI/res';
import { convertEmailToPlainText } from 'UI/utils';

import { buildAttemptsFormKey, getAttemptsFormKeys } from '../../BulkSequences.utils';
import { BulkEmailDrawerSequencesPreview } from '../BulkEmailDrawerSequencesPreview';
import { BulkEmailMultipleAttemptsForm } from '../BulkEmailMultipleAttemptsForm';

import { getStepsDrawerFinalTitle } from './BulkEmailStepsDrawer.utils';

const { sequences: sequencesCopies } = strings.bulkEmails.forms;
const attemptsKey = BulkDrawerFormFieldsMap.Attempts.key;

export const BulkEmailStepsDrawer = ({
  bulkConfig = {},
  onClose,
  shouldEdit = false,
  step = {}
}) => {
  const commonClasses = useBulkDrawerCommonStyles();
  const dispatch = useDispatch();

  const form = useForm({
    defaultValues: {
      [attemptsKey]: [step.id],
      [BulkDrawerFormFieldsMap.To.key]: bulkConfig.searchProject,
      [BulkDrawerFormFieldsMap.BulkScope.key]: bulkConfig.scope,
      [BulkDrawerFormFieldsMap.MarketCandidates.key]: bulkConfig.candidates,
      [BulkDrawerFormFieldsMap.JobOrderRecruitment.key]: bulkConfig.jobOrders
    }
  });
  const { handleSubmit, register, triggerValidation, unregister } = form;

  const [uiState, setUiState] = useState({
    isFormLoading: false,
    isFormSubmitting: false,
    isDiscardDialogOpen: false,
    shouldShowPreview: false
  });

  useEffect(() => {
    register({ name: attemptsKey });
    register({ name: BulkDrawerFormFieldsMap.To.key });
    register({ name: BulkDrawerFormFieldsMap.BulkScope.key });

    if (bulkConfig.candidates) {
      register({ name: BulkDrawerFormFieldsMap.MarketCandidates.key });
    } else if (bulkConfig.jobOrders) {
      register({ name: BulkDrawerFormFieldsMap.JobOrderRecruitment.key });
    }

    return () => {
      unregister([
        attemptsKey,
        BulkDrawerFormFieldsMap.To.key,
        BulkDrawerFormFieldsMap.BulkScope.key,
        BulkDrawerFormFieldsMap.MarketCandidates.key,
        BulkDrawerFormFieldsMap.JobOrderRecruitment.key
      ]);
    };
  }, [bulkConfig.candidates, bulkConfig.jobOrders, register, unregister]);

  const togglers = {
    discardDialog:
      ({ open }) =>
      () =>
        setUiState(prev => ({ ...prev, isDiscardDialogOpen: open })),
    showPreview:
      ({ open }) =>
      () =>
        setUiState(prev => ({ ...prev, shouldShowPreview: open }))
  };

  const handleSubmitStep = async formData => {
    const baseKey = buildAttemptsFormKey(step.id);
    const payload = { bulkId: bulkConfig.id };

    if (shouldEdit) {
      const delayDays = formData[baseKey].triggerDays.value;
      const { emailBody } = formData[baseKey];
      const { subject } = formData[baseKey];

      payload.delayDays = delayDays;
      payload.emailBody = emailBody;
      payload.stepId = step.id;
      payload.subject = subject;
      payload.text = convertEmailToPlainText(emailBody);
    } else {
      payload.steps = [];
      getAttemptsFormKeys(formData[BulkDrawerFormFieldsMap.Attempts.key]).forEach((key, index) => {
        const { emailBody, promptId, subject, triggerDays } = formData[key];

        payload.steps.push({
          html: emailBody,
          prompt_id: promptId || null,
          sending_after_next_days: triggerDays.value,
          sequence_number: step.currentStep + index,
          subject,
          text: convertEmailToPlainText(emailBody)
        });
      });
    }

    const { alert, success } = await (shouldEdit
      ? editSequenceStep(payload)
      : addSequenceSteps(payload));

    dispatch(showAlert(alert));

    if (success) {
      dispatch(setBulkEmailStatus({ reloadEmailList: true }));
      onClose();
    } else {
      setUiState(prev => ({ ...prev, isFormSubmitting: false }));
    }
  };

  const submitStepsHandler = handleSubmit(async formData => {
    if (uiState.shouldShowPreview) {
      togglers.discardDialog({ open: false })();
    }

    const execSubmit = async () => {
      setUiState(prev => ({ ...prev, isFormSubmitting: true }));
      await handleSubmitStep(formData);
    };

    shouldEdit
      ? dispatch(
          confirm({
            ...sequencesCopies.editStep.saveChangesDialog,
            message: strings.formatString(sequencesCopies.editStep.saveChangesDialog.message, {
              currentStep: step.currentStep
            }),
            severity: 'warning',
            withButtons: 'YesNo',
            onConfirm: async ok => {
              if (ok) await execSubmit();
            }
          })
        )
      : await execSubmit();
  });

  const saveIconRenderer = useCallback(() => {
    if (!uiState.isFormSubmitting) return null;
    return <CircularProgress size={20} color="inherit" />;
  }, [uiState.isFormSubmitting]);

  const handleSequencePreview = async () => {
    const areFieldsFilledCorrectly = await triggerValidation();
    if (areFieldsFilledCorrectly) togglers.showPreview({ open: true })();
  };

  const drawerButtonDefinitions = [
    {
      props: {
        text: sequencesCopies.addSteps.buttonsDefinitions.cancel,
        variant: 'outlined',
        onClick: togglers.discardDialog({ open: true })
      },
      visible: !uiState.shouldShowPreview
    },
    {
      props: {
        text: sequencesCopies.addSteps.buttonsDefinitions.preview,
        variant: 'contained',
        onClick: handleSequencePreview
      },
      visible: !uiState.shouldShowPreview
    },
    {
      props: {
        disabled: uiState.isFormSubmitting,
        text: sequencesCopies.addSteps.buttonsDefinitions.back,
        variant: 'outlined',
        onClick: togglers.showPreview({ open: false })
      },
      visible: uiState.shouldShowPreview
    },
    {
      props: {
        disabled: uiState.isFormSubmitting,
        icon: saveIconRenderer,
        iconPosition: 'right',
        text: sequencesCopies.addSteps.buttonsDefinitions.save,
        variant: 'contained',
        onClick: submitStepsHandler
      },
      visible: uiState.shouldShowPreview
    }
  ];

  return (
    <>
      <FPDrawer
        CardHeaderProps={{
          avatar: uiState.shouldShowPreview ? (
            <FPIconButton
              disabled={uiState.isFormSubmitting}
              onClick={togglers.showPreview({ open: false })}
              iconMode="custom"
              icon={SvgArrowLeft}
              tooltipProps={{ title: sequencesCopies.addSteps.buttonsDefinitions.back }}
            />
          ) : null,
          title: getStepsDrawerFinalTitle({
            shouldEdit,
            shouldShowPreview: uiState.shouldShowPreview
          }),
          titleTypographyProps: { variant: 'h5' }
        }}
        footer={
          <FPButtonBuilder
            GridContainerProps={{ spacing: 2 }}
            buttonDefinitions={drawerButtonDefinitions}
          />
        }
        FPDrawerProps={{
          open: true,
          classes: { paper: commonClasses.drawerRoot }
        }}
        contentContainerClassName={commonClasses.contentContainer}
        hasPaddingContent={false}
        onClose={togglers.discardDialog({ open: true })}
      >
        <StatusManager status={uiState.isFormLoading ? UIStatus.Loading : UIStatus.Success}>
          <FormContext {...form}>
            <When condition={uiState.shouldShowPreview}>
              <BulkEmailDrawerSequencesPreview currentStep={step.currentStep} />
            </When>
            <Collapse in={!uiState.shouldShowPreview} timeout={0}>
              <BulkEmailMultipleAttemptsForm
                onFetchingSmartTags={({ loading }) =>
                  setUiState(prev => ({ ...prev, isFormLoading: loading }))
                }
                step={step}
                isFormBeingEdited={shouldEdit}
              />
            </Collapse>
          </FormContext>
        </StatusManager>
      </FPDrawer>
      <When condition={uiState.isDiscardDialogOpen}>
        <DiscardBulkDialog
          isBulkBeingEdited
          onDiscard={onClose}
          onKeepEditing={togglers.discardDialog({ open: false })}
          onSaveChanges={uiState.shouldShowPreview ? submitStepsHandler : null}
          selectedMenuItem="sequenceAttempt"
        />
      </When>
    </>
  );
};
